import React, { useMemo } from 'react';
import { Formik, Form, Field } from 'formik';
import {
  Box,
  MenuItem,
  Button,
  FormControl,
  Select,
  CircularProgress,
  Typography,
  TextField,
  FormControlLabel,
  Switch,
  FormHelperText,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../../../../store';
import { useDataAgentExampleQueryManagement } from './AddEditDataAgentExampleQueryManagement';
import { useGetDataAgentExampleQueriesQuery } from '../../../../api/dataAgentApiSlice';
import QueryEditor from '../../../sqlQueries/QueryEditor';
import { HelpCallout } from '../../../../components/HelpCallout';

interface AddEditDataAgentExampleQueryUIFormProps {
  onCancel: () => void;
  initialData?: any;
  dataAgentId: string;
}

const AddEditDataAgentExampleQueryUIForm: React.FC<AddEditDataAgentExampleQueryUIFormProps> = ({
  onCancel,
  initialData,
  dataAgentId,
}) => {
  const { t } = useTranslation();
  const { selectedCustomerId: customerId, selectedClientId: clientId } = useSelector(
    (state: RootState) => state.vaultilake,
  );
  const { validationSchema, handleSubmit, handleUpdate } = useDataAgentExampleQueryManagement();

  const isEditMode = !!initialData?.data_agent_example_query_id;

  const {
    data: existingQueriesData,
    isLoading: isLoadingQuery,
    isError: isErrorQuery,
  } = useGetDataAgentExampleQueriesQuery(
    {
      customer_id: customerId ?? undefined,
      client_id: clientId ?? undefined,
      data_agent_id: dataAgentId,
      data_agent_example_query_id: initialData?.data_agent_example_query_id,
    },
    {
      skip: !isEditMode || !initialData?.data_agent_example_query_id,
    },
  );

  const existingQuery = useMemo(() => {
    if (!isEditMode || !existingQueriesData || !Array.isArray(existingQueriesData)) {
      return null;
    }
    return existingQueriesData.find(
      (q: any) => q.data_agent_example_query_id === initialData?.data_agent_example_query_id,
    );
  }, [existingQueriesData, isEditMode, initialData]);

  const initialValues = {
    dataAgentId,
    customerId,
    clientId,
    question: isEditMode && existingQuery ? existingQuery.question : initialData?.question || '',
    query: isEditMode && existingQuery ? existingQuery.query : initialData?.query || '',
    queryLanguage: isEditMode && existingQuery ? existingQuery.query_language || 'SQL' : 'SQL',
    description: isEditMode && existingQuery ? existingQuery.description || '' : initialData?.description || '',
    expectedResult:
      isEditMode && existingQuery ? existingQuery.expected_result || '' : initialData?.expected_result || '',
    isActive: isEditMode && existingQuery ? existingQuery.is_active : (initialData?.is_active ?? true),
  };

  const handleFormSubmit = async (values: any, { setSubmitting }: any) => {
    try {
      if (isEditMode && initialData?.data_agent_example_query_id) {
        const result = await handleUpdate(initialData.data_agent_example_query_id, values);
        if (!result.success) {
          setSubmitting(false);
          return;
        }
      } else {
        const result = await handleSubmit(values);
        if (!result.success) {
          setSubmitting(false);
          return;
        }
      }
      onCancel();
    } catch (error) {
      console.error('Error saving example query:', error);
    } finally {
      setSubmitting(false);
    }
  };

  if (isEditMode && isLoadingQuery) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (isEditMode && isErrorQuery) {
    return (
      <Box p={2}>
        <Typography color="error">{t('dataAgents.exampleQueries.form.errorLoadingQueryData')}</Typography>
      </Box>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleFormSubmit}
      enableReinitialize={isEditMode}
    >
      {({ values, setFieldValue, isSubmitting, errors, touched }) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          {/* Scrollable Content */}
          <Box sx={{ flex: 1, overflow: 'auto', pr: 1 }}>
            <Form>
              <Box sx={{ width: '100%' }}>
                {/* Question Field */}
                <Box sx={{ mb: 2 }}>
                  <HelpCallout
                    label={
                      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                        {t('dataAgents.exampleQueries.form.questionLabel')}
                        <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>
                          *
                        </Box>
                      </span>
                    }
                    labelId="question-label"
                    fieldId="question"
                    helpText={t('dataAgents.exampleQueries.form.helpTexts.question')}
                  />
                  <Field
                    as={TextField}
                    fullWidth
                    variant="outlined"
                    name="question"
                    size="small"
                    placeholder={t('dataAgents.exampleQueries.form.questionPlaceholder')}
                    error={touched.question && Boolean(errors.question)}
                    helperText={touched.question && errors.question}
                  />
                </Box>

                {/* Query Language Dropdown */}
                <Box sx={{ mb: 2 }}>
                  <HelpCallout
                    label={
                      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                        {t('dataAgents.exampleQueries.form.queryLanguageLabel')}
                        <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>
                          *
                        </Box>
                      </span>
                    }
                    labelId="querylanguage-label"
                    fieldId="queryLanguage"
                    helpText={t('dataAgents.exampleQueries.form.helpTexts.queryLanguage')}
                  />
                  <FormControl fullWidth size="small" error={touched.queryLanguage && Boolean(errors.queryLanguage)}>
                    <Field as={Select} id="query-language" name="queryLanguage" displayEmpty>
                      <MenuItem value="SQL">SQL</MenuItem>
                    </Field>
                    {touched.queryLanguage && errors.queryLanguage && (
                      <FormHelperText>{String(errors.queryLanguage)}</FormHelperText>
                    )}
                  </FormControl>
                </Box>

                {/* Query Editor */}
                <Box sx={{ mb: 2 }}>
                  <HelpCallout
                    label={
                      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                        {t('dataAgents.exampleQueries.form.queryLabel')}
                        <Box component="span" sx={{ color: 'error.main', ml: 0.5 }}>
                          *
                        </Box>
                      </span>
                    }
                    labelId="query-label"
                    fieldId="query"
                    helpText={t('dataAgents.exampleQueries.form.helpTexts.query')}
                  />
                  <Box
                    sx={{
                      height: 300,
                      border: '1px solid',
                      borderColor: touched.query && errors.query ? 'error.main' : 'divider',
                      borderRadius: 1,
                      overflow: 'hidden',
                    }}
                  >
                    <QueryEditor
                      value={values.query}
                      onChange={(value) => setFieldValue('query', value)}
                      height={300}
                    />
                  </Box>
                  {touched.query && errors.query && (
                    <FormHelperText error sx={{ ml: 1.75, mt: 0.5 }}>
                      {String(errors.query)}
                    </FormHelperText>
                  )}
                </Box>

                {/* Description Field */}
                <Box sx={{ mb: 2 }}>
                  <HelpCallout
                    label={t('dataAgents.exampleQueries.form.descriptionLabel')}
                    labelId="description-label"
                    fieldId="description"
                    helpText={t('dataAgents.exampleQueries.form.helpTexts.description')}
                  />
                  <Field
                    as={TextField}
                    fullWidth
                    multiline
                    rows={3}
                    variant="outlined"
                    name="description"
                    size="small"
                    placeholder={t('dataAgents.exampleQueries.form.descriptionPlaceholder')}
                    error={touched.description && Boolean(errors.description)}
                    helperText={touched.description && errors.description}
                  />
                </Box>

                {/* Expected Result Field */}
                <Box sx={{ mb: 2 }}>
                  <HelpCallout
                    label={t('dataAgents.exampleQueries.form.expectedResultLabel')}
                    labelId="expectedresult-label"
                    fieldId="expectedResult"
                    helpText={t('dataAgents.exampleQueries.form.helpTexts.expectedResult')}
                  />
                  <Field
                    as={TextField}
                    fullWidth
                    multiline
                    rows={3}
                    variant="outlined"
                    name="expectedResult"
                    size="small"
                    placeholder={t('dataAgents.exampleQueries.form.expectedResultPlaceholder')}
                    error={touched.expectedResult && Boolean(errors.expectedResult)}
                    helperText={touched.expectedResult && errors.expectedResult}
                  />
                </Box>

                {/* Is Active Toggle */}
                <Box sx={{ mb: 2, p: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.isActive}
                        onChange={(e) => setFieldValue('isActive', e.target.checked)}
                        color="primary"
                        size="small"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {t('dataAgents.exampleQueries.form.activeStatusLabel')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {t('dataAgents.exampleQueries.form.activeStatusDescription')}
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
              </Box>
            </Form>
          </Box>

          {/* Sticky Action Buttons at Bottom */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1.5,
              pt: 2,
              pb: 1,
              px: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              flexShrink: 0,
            }}
          >
            <Button variant="outlined" onClick={onCancel} sx={{ textTransform: 'none' }} disabled={isSubmitting}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                const form = document.querySelector('form');
                if (form) {
                  form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
                }
              }}
              disabled={isSubmitting}
              sx={{ textTransform: 'none' }}
            >
              {isSubmitting ? (
                <CircularProgress size={20} />
              ) : isEditMode ? (
                t('dataAgents.exampleQueries.form.update')
              ) : (
                t('common.create')
              )}
            </Button>
          </Box>
        </Box>
      )}
    </Formik>
  );
};

export default AddEditDataAgentExampleQueryUIForm;
